import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/auth-context'
import { notifySlackNewIssue } from '../utils/slack'

const BUCKET_NAME = 'issue-attachments'

export function useIssues() {
	const { customerId, isAdmin } = useAuth()
	const [issues, setIssues] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)

	const fetchIssues = useCallback(async () => {
		if (!customerId && !isAdmin) {
			setLoading(false)
			return
		}

		try {
			setLoading(true)
			setError(null)

			let query = supabase
				.from('issues')
				.select(`
					*,
					issue_attachments(*),
					customer:customers(id, name)
				`)
				.order('created_at', { ascending: false })

			if (!isAdmin && customerId) {
				query = query.eq('customer_id', customerId)
			}

			const { data, error: fetchError } = await query

			if (fetchError) throw fetchError
			setIssues(data || [])
		} catch (err) {
			console.error('Error fetching issues:', err)
			setError(err.message)
			setIssues([])
		} finally {
			setLoading(false)
		}
	}, [customerId, isAdmin])

	const createIssue = useCallback(async (issueData, files = []) => {
		const { data: { user } } = await supabase.auth.getUser()
		if (!user) throw new Error('Not authenticated')
		if (!customerId && !isAdmin) throw new Error('No customer assignment')

		const effectiveCustomerId = issueData.customer_id ?? customerId
		if (!effectiveCustomerId) throw new Error('Customer ID required')

		// 1. Create the issue first
		const { data: issue, error: issueError } = await supabase
			.from('issues')
			.insert({
				customer_id: effectiveCustomerId,
				sensor_id: issueData.sensor_id || null,
				created_by: user.id,
				title: issueData.title,
				description: issueData.description || null,
				status: 'open',
				priority: issueData.priority || 'medium'
			})
			.select()
			.single()

		if (issueError) throw issueError

		// 2. Upload files and create attachment records
		if (files.length > 0) {
			const pathPrefix = `${effectiveCustomerId}/${issue.id}`
			const attachmentInserts = []

			for (const file of files) {
				const safeName = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`
				const filePath = `${pathPrefix}/${safeName}`

				const { error: uploadError } = await supabase.storage
					.from(BUCKET_NAME)
					.upload(filePath, file, {
						contentType: file.type || 'application/octet-stream',
						upsert: false
					})

				if (uploadError) {
					console.error('Upload failed for', file.name, uploadError)
					continue
				}

				attachmentInserts.push({
					issue_id: issue.id,
					file_name: file.name,
					file_path: filePath,
					file_size: file.size,
					mime_type: file.type || null,
					uploaded_by: user.id
				})
			}

			if (attachmentInserts.length > 0) {
				const { error: attachError } = await supabase
					.from('issue_attachments')
					.insert(attachmentInserts)

				if (attachError) console.error('Error creating attachment records:', attachError)
			}
		}

		// Notify Slack via Edge Function (fire-and-forget, avoids CORS)
		const { data: customer } = await supabase
			.from('customers')
			.select('name')
			.eq('id', effectiveCustomerId)
			.single()
		notifySlackNewIssue(supabase, issue, customer?.name).catch(() => {})

		await fetchIssues()
		return issue
	}, [customerId, isAdmin, fetchIssues])

	const updateIssue = useCallback(async (issueId, updates) => {
		const { error } = await supabase
			.from('issues')
			.update(updates)
			.eq('id', issueId)

		if (error) throw error
		await fetchIssues()
	}, [fetchIssues])

	const addAttachmentsToIssue = useCallback(async (issueId, files) => {
		const { data: { user } } = await supabase.auth.getUser()
		if (!user) throw new Error('Not authenticated')

		const issue = issues.find(i => i.id === issueId)
		if (!issue) throw new Error('Issue not found')
		const effectiveCustomerId = issue.customer_id

		const pathPrefix = `${effectiveCustomerId}/${issueId}`
		const attachmentInserts = []

		for (const file of files) {
			const safeName = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`
			const filePath = `${pathPrefix}/${safeName}`

			const { error: uploadError } = await supabase.storage
				.from(BUCKET_NAME)
				.upload(filePath, file, {
					contentType: file.type || 'application/octet-stream',
					upsert: false
				})

			if (uploadError) {
				console.error('Upload failed for', file.name, uploadError)
				continue
			}

			attachmentInserts.push({
				issue_id: issueId,
				file_name: file.name,
				file_path: filePath,
				file_size: file.size,
				mime_type: file.type || null,
				uploaded_by: user.id
			})
		}

		if (attachmentInserts.length > 0) {
			const { error: attachError } = await supabase
				.from('issue_attachments')
				.insert(attachmentInserts)

			if (attachError) throw attachError
		}

		await fetchIssues()
	}, [issues, fetchIssues])

	const getAttachmentUrl = useCallback(async (filePath, expiresIn = 3600) => {
		const { data, error } = await supabase.storage
			.from(BUCKET_NAME)
			.createSignedUrl(filePath, expiresIn)

		if (error) throw error
		return data.signedUrl
	}, [])

	// Realtime subscription
	useEffect(() => {
		if (!customerId && !isAdmin) return

		fetchIssues()

		const channel = supabase
			.channel('issues-changes')
			.on(
				'postgres_changes',
				{
					event: '*',
					schema: 'public',
					table: 'issues'
				},
				() => {
					fetchIssues()
				}
			)
			.subscribe()

		return () => {
			channel.unsubscribe()
		}
	}, [customerId, isAdmin, fetchIssues])

	return {
		issues,
		loading,
		error,
		refetch: fetchIssues,
		createIssue,
		updateIssue,
		addAttachmentsToIssue,
		getAttachmentUrl
	}
}
